/**
 * The consumer API client.
 *
 * ## Thin on purpose
 *
 * It sends a request and unwraps the envelope. It does not transform, merge,
 * sort, price or filter anything — every one of those decisions belongs to a
 * module that already owns it, and a client that started making them would be
 * a third place the marketplace disagrees with itself.
 *
 * ## Errors are modelled, not thrown away
 *
 * A page needs to tell "nothing matched" from "we could not reach the
 * marketplace", because the first is a normal result and the second is an
 * outage. `ApiError` carries the status so a 404 renders a not-found page and a
 * 500 renders a retry.
 */

export interface Envelope<T> {
  readonly data: T;
  readonly meta?: unknown;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Where the API lives.
 *
 * Same-origin by default, so the browser sends no cross-origin request and no
 * CORS configuration is needed for the ordinary deployment. `VITE_API_BASE_URL`
 * overrides it for local development against a separately-hosted API.
 */
const BASE_URL: string = (import.meta.env?.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  let response: Response;

  try {
    response = await fetch(`${BASE_URL}${path}`, {
      headers: { accept: 'application/json' },
      ...(signal !== undefined ? { signal } : {}),
    });
  } catch (error) {
    // Network-level: offline, DNS, a dropped connection. Distinct from a
    // marketplace that answered and said no.
    if (error instanceof Error && error.name === 'AbortError') throw error;
    throw new ApiError('We could not reach the marketplace.', 0);
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => undefined)) as
      { error?: { message?: string; code?: string } } | undefined;

    throw new ApiError(body?.error?.message ?? 'Something went wrong.', response.status, body?.error?.code);
  }

  const envelope = (await response.json()) as Envelope<T>;
  return envelope.data;
}

// -- The shapes the pages render ---------------------------------------------
//
// Mirrors of the BFF's consumer views, declared here rather than imported so
// the browser bundle does not pull an engine package in. They are deliberately
// structural: if the BFF narrows a field, this stops compiling at the point of
// use rather than silently rendering `undefined`.

export interface ConsumerPrice {
  readonly resolved: boolean;
  readonly currency?: string;
  readonly amountMinor?: number;
  readonly compareAtMinor?: number;
  readonly savingMinor?: number;
  readonly promotionNames?: readonly string[];
}

export interface ConsumerOfferCard {
  readonly id: string;
  readonly kind: 'PRODUCT' | 'SERVICE';
  readonly title: string;
  readonly summary?: string;
  readonly brandName?: string;
  readonly storefrontId: string;
  readonly storefrontSlug: string;
  readonly storefrontName: string;
  readonly fulfilmentModes: readonly string[];
  readonly nationwide: boolean;
  readonly restriction: {
    readonly ageRestricted: boolean;
    readonly consultationRequired: boolean;
    readonly prescriptionRequired: boolean;
  };
  readonly price: ConsumerPrice;
  readonly bookability?: { readonly bookable: boolean; readonly nextAvailableAt?: string };
  readonly stock?: { readonly known: boolean; readonly inStock?: boolean };
}

export interface ConsumerCategory {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly description?: string;
  readonly depth: number;
  readonly itemKinds: readonly string[];
}

export interface ConsumerSection {
  readonly key: string;
  readonly title: string;
  readonly kind?: 'PRODUCT' | 'SERVICE';
  readonly offers: readonly ConsumerOfferCard[];
}

export interface ConsumerHomepage {
  readonly sections: readonly ConsumerSection[];
  readonly categories: readonly ConsumerCategory[];
  readonly near?: { readonly postcode?: string };
  readonly populated: boolean;
}

export interface ConsumerPageInfo {
  readonly nextCursor?: string;
  readonly hasMore: boolean;
  readonly totalMatched?: number;
}

export interface ConsumerSearchResults {
  readonly results: readonly ConsumerOfferCard[];
  readonly pageInfo: ConsumerPageInfo;
}

export interface ConsumerLocation {
  readonly id: string;
  readonly type: string;
  readonly town?: string;
  readonly postcode?: string;
}

export interface ConsumerOfferDetail {
  readonly id: string;
  readonly kind: 'PRODUCT' | 'SERVICE';
  readonly title: string;
  readonly summary?: string;
  readonly description?: string;
  readonly brandName?: string;
  readonly categoryIds: readonly string[];
  readonly storefront: {
    readonly id: string;
    readonly slug: string;
    readonly name: string;
    readonly shortDescription?: string;
    readonly fulfilmentModes: readonly string[];
  };
  readonly locations: readonly ConsumerLocation[];
  readonly fulfilmentModes: readonly string[];
  readonly restriction: ConsumerOfferCard['restriction'];
  readonly price: ConsumerPrice;
  readonly bookability?: { readonly bookable: boolean; readonly nextAvailableAt?: string };
}

export interface ConsumerStorefrontDetail {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly shortDescription?: string;
  readonly about?: string;
  readonly specialisms: readonly string[];
  readonly fulfilmentModes: readonly string[];
  readonly locations: readonly ConsumerLocation[];
  readonly offers: readonly ConsumerOfferCard[];
  readonly pageInfo: ConsumerPageInfo;
}

export const consumerApi = {
  homepage: (query: string, signal?: AbortSignal) =>
    get<ConsumerHomepage>(`/consumer/homepage${query}`, signal),

  search: (query: string, signal?: AbortSignal) =>
    get<ConsumerSearchResults>(`/consumer/search${query}`, signal),

  category: (categoryId: string, query: string, signal?: AbortSignal) =>
    get<ConsumerSearchResults>(
      `/consumer/categories/${encodeURIComponent(categoryId)}/offers${query}`,
      signal,
    ),

  offer: (listingId: string, signal?: AbortSignal) =>
    get<ConsumerOfferDetail>(`/consumer/offers/${encodeURIComponent(listingId)}`, signal),

  storefront: (slug: string, query: string, signal?: AbortSignal) =>
    get<ConsumerStorefrontDetail>(`/consumer/storefronts/${encodeURIComponent(slug)}${query}`, signal),
};

// -- PENNY earn badges ----------------------------------------------------------
//
// "Earn X PENNY", calculated by the server from the marketplace's own price and
// the reward programme in force — the browser sends listing ids only. A listing
// that earns nothing (including every pharmacy or prescription medicine) comes
// back as 0 and renders no badge.
export async function earnPreview(
  listingIds: readonly string[],
  signal?: AbortSignal,
): Promise<ReadonlyMap<string, number>> {
  if (listingIds.length === 0) return new Map();
  const response = await fetch(`${BASE_URL}/consumer/penny-public/earn-preview`, {
    method: 'POST',
    headers: { accept: 'application/json', 'content-type': 'application/json' },
    body: JSON.stringify({ listingIds: [...new Set(listingIds)].slice(0, 60) }),
    ...(signal !== undefined ? { signal } : {}),
  });
  if (!response.ok) return new Map();
  const envelope = (await response.json()) as Envelope<{
    items: readonly { listingId: string; units: number }[];
  }>;
  return new Map(envelope.data.items.map((item) => [item.listingId, item.units]));
}

// -- PENNY Wellness challenges ----------------------------------------------------
//
// The challenges open to join, as the server lists them — names, rules, rewards
// and sponsors. Never a person, never anyone's progress or activity.
export interface PublicWellnessChallenge {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly type: 'DAILY_TARGET' | 'CUMULATIVE' | 'ACTIVE_DAYS' | 'STREAK';
  readonly metric: 'STEPS' | 'DISTANCE' | 'ACTIVE_MINUTES' | 'ENERGY';
  readonly target: number;
  readonly dailyThreshold?: number;
  readonly repeat: 'ONCE' | 'EACH_DAY';
  readonly startDate: string;
  readonly endDate: string;
  readonly reward:
    | { readonly kind: 'FIXED'; readonly units: number }
    | { readonly kind: 'TIERED'; readonly tiers: readonly { readonly at: number; readonly units: number }[] };
  readonly maxUnits: number;
  readonly funding: 'JUST_TREATED' | 'MERCHANT';
  readonly spendableOnlyAtSponsor: boolean;
  readonly sponsor: { readonly storefrontId: string; readonly name: string } | null;
}

export async function wellnessChallenges(
  storefrontId?: string,
  signal?: AbortSignal,
): Promise<readonly PublicWellnessChallenge[]> {
  const query = storefrontId === undefined ? '' : `?storefrontId=${encodeURIComponent(storefrontId)}`;
  const response = await fetch(`${BASE_URL}/consumer/wellness-public/challenges${query}`, {
    headers: { accept: 'application/json' },
    ...(signal !== undefined ? { signal } : {}),
  });
  if (!response.ok) return [];
  const envelope = (await response.json()) as Envelope<{ challenges: readonly PublicWellnessChallenge[] }>;
  return envelope.data.challenges;
}
